import type { Address, Hash } from 'viem';
import { create } from 'zustand';

export type TxStage = 'wallet' | 'pending' | 'unknown' | 'confirmed' | 'failed' | 'cancelled';
export type TxOperation = 'buy' | 'sell' | 'claim';
export type TxRecord = {
  id: string;
  chainId: number;
  account: Address;
  operation: TxOperation;
  marketAddress: Address;
  step: 'approve' | 'execute';
  stage: TxStage;
  hash?: Hash;
  startedAt: number;
  approvalConfirmed?: boolean;
  replacementChanged?: boolean;
};
export type TransactionState = {
  records: Record<string, TxRecord>;
  begin: (record: TxRecord) => boolean;
  update: (
    id: string,
    patch: Partial<Pick<TxRecord, 'stage' | 'hash' | 'step' | 'approvalConfirmed' | 'replacementChanged'>>
  ) => void;
  findActive: (chainId: number, account: Address) => TxRecord | undefined;
  /** Newest dismissed finished-result start time per chain+account (see dismissKey). */
  dismissedThrough: Record<string, number>;
  /** Hides the finished result started at `startedAt` and every older one for the chain+account. */
  dismissThrough: (chainId: number, account: Address, startedAt: number) => void;
};
export const TRANSACTION_STORAGE_KEY = 'dpin-pending-transactions-v1';
export const dismissKey = (chainId: number, account: Address | string) => `${chainId}:${account.toLowerCase()}`;
export const isActiveTransaction = (record: TxRecord) => ['wallet', 'pending', 'unknown'].includes(record.stage);

const metadata = (record: TxRecord): TxRecord => ({
  id: record.id,
  chainId: record.chainId,
  account: record.account,
  operation: record.operation,
  marketAddress: record.marketAddress,
  step: record.step,
  stage: record.stage,
  hash: record.hash,
  startedAt: record.startedAt,
  approvalConfirmed: record.approvalConfirmed,
  replacementChanged: record.replacementChanged,
});

const restore = (): Record<string, TxRecord> => {
  try {
    const records: unknown = JSON.parse(sessionStorage.getItem(TRANSACTION_STORAGE_KEY) ?? '{}');
    if (!records || typeof records !== 'object') return {};
    return Object.fromEntries(
      Object.entries(records)
        .filter(([id, value]) => {
          const r = value as TxRecord;
          return (
            r &&
            r.id === id &&
            Number.isInteger(r.chainId) &&
            typeof r.account === 'string' &&
            /^0x[0-9a-f]{40}$/i.test(r.account) &&
            typeof r.marketAddress === 'string' &&
            /^0x[0-9a-f]{40}$/i.test(r.marketAddress) &&
            ['buy', 'sell', 'claim'].includes(r.operation) &&
            ['approve', 'execute'].includes(r.step) &&
            isActiveTransaction(r) &&
            Number.isFinite(r.startedAt) &&
            (!r.hash || /^0x[0-9a-f]{64}$/i.test(r.hash))
          );
        })
        .map(([id, r]) => [id, { ...metadata(r), stage: r.hash ? 'pending' : 'unknown' }])
    );
  } catch {
    return {};
  }
};

export const useTransactionStore = create<TransactionState>((set, get) => ({
  records: restore(),
  // In memory only: finished records are not restored on reload, so there is
  // nothing for a persisted dismissal to hide.
  dismissedThrough: {},
  dismissThrough: (chainId, account, startedAt) =>
    set(state => {
      const key = dismissKey(chainId, account);
      if ((state.dismissedThrough[key] ?? -Infinity) >= startedAt) return state;
      return { dismissedThrough: { ...state.dismissedThrough, [key]: startedAt } };
    }),
  begin: record => {
    if (get().records[record.id] || get().findActive(record.chainId, record.account)) return false;
    set(state => ({
      records: { ...state.records, [record.id]: { ...record, account: record.account.toLowerCase() as Address } },
    }));
    return true;
  },
  update: (id, patch) =>
    set(state => {
      const record = state.records[id];
      if (!record || !isActiveTransaction(record)) return state;
      return { records: { ...state.records, [id]: { ...record, ...patch } } };
    }),
  findActive: (chainId, account) =>
    Object.values(get().records).find(
      record =>
        record.chainId === chainId && record.account.toLowerCase() === account.toLowerCase() && isActiveTransaction(record)
    ),
}));

useTransactionStore.subscribe(state => {
  try {
    sessionStorage.setItem(
      TRANSACTION_STORAGE_KEY,
      JSON.stringify(
        Object.fromEntries(
          Object.entries(state.records)
            .filter(([, record]) => isActiveTransaction(record))
            .map(([id, record]) => [id, metadata(record)])
        )
      )
    );
  } catch {
    /* The in-memory lock remains authoritative if storage is unavailable. */
  }
});

const executing = new Set<string>();
export const startTransaction = (record: TxRecord): string | undefined => {
  const active = useTransactionStore.getState().findActive(record.chainId, record.account);
  if (active) {
    if (
      !active.approvalConfirmed ||
      active.hash ||
      active.operation !== record.operation ||
      active.marketAddress.toLowerCase() !== record.marketAddress.toLowerCase() ||
      executing.has(active.id)
    )
      return;
    executing.add(active.id);
    useTransactionStore.getState().update(active.id, { stage: 'wallet' });
    return active.id;
  }
  if (!useTransactionStore.getState().begin(record)) return;
  executing.add(record.id);
  return record.id;
};
export const finishTransactionRequest = (id: string) => {
  executing.delete(id);
};
export const canAcknowledgeWalletCancellation = (record: TxRecord) =>
  !record.hash && record.stage === 'unknown' && !record.approvalConfirmed && !executing.has(record.id);
export const canResumeTransaction = (record: TxRecord) =>
  !!record.approvalConfirmed && !record.hash && !executing.has(record.id);
export const failTransactionRequest = (id: string) => {
  const record = useTransactionStore.getState().records[id];
  if (record && !record.hash && record.stage === 'wallet') useTransactionStore.getState().update(id, { stage: 'failed' });
};

/** An ambiguous wallet/RPC send failure may already have broadcast a transaction. */
export const sendTransaction = async (id: string, send: () => Promise<Hash>): Promise<Hash> => {
  // Approval permits reconfirmation only before execution reaches the wallet.
  // An ambiguous send response must never reopen this approved intent.
  useTransactionStore.getState().update(id, { approvalConfirmed: false });
  try {
    return await send();
  } catch (error) {
    let cause: unknown = error;
    let rejected = false;
    for (let depth = 0; cause && typeof cause === 'object' && depth < 10; depth++) {
      const item = cause as { code?: number; name?: string; cause?: unknown };
      rejected ||= item.code === 4001 || item.name === 'UserRejectedRequestError';
      cause = item.cause;
    }
    useTransactionStore.getState().update(id, { stage: rejected ? 'cancelled' : 'unknown' });
    throw rejected ? error : new Error('transaction-unknown');
  }
};
